import { useState, useCallback } from 'react';
import { Save, Download, Share2, X, Check, Copy, CheckCircle } from 'lucide-react';
import { useMoleculeStore, useUIStore } from '../../store';
import { Molecule } from '../../types';

interface SavePanelProps {
  onClose?: () => void;
}

export function SavePanel({ onClose }: SavePanelProps) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  const { currentMolecule, addToLibrary } = useMoleculeStore();
  const { showToast, setSidebarTab } = useUIStore();

  const handleSave = useCallback(async () => {
    if (!currentMolecule) {
      showToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Error',
        message: 'No molecule to save',
      });
      return;
    }

    if (!name.trim()) {
      showToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Error',
        message: 'Please enter a name',
      });
      return;
    }

    setIsSaving(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 500));

      const savedMolecule = {
        id: Date.now().toString(),
        molecule: { ...currentMolecule, name: name.trim() },
        createdAt: Date.now(),
        notes: notes.trim(),
        tags: [],
      };

      addToLibrary(savedMolecule);
      setSaved(true);

      showToast({
        id: Date.now().toString(),
        type: 'success',
        title: 'Saved Successfully',
        message: `${name} has been saved to your library`,
      });

      setTimeout(() => {
        setSidebarTab('library');
        if (onClose) {
          onClose();
        }
      }, 1500);
    } catch (error) {
      showToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Error',
        message: 'Failed to save molecule',
      });
    } finally {
      setIsSaving(false);
    }
  }, [currentMolecule, name, notes, addToLibrary, showToast, setSidebarTab, onClose]);

  if (!currentMolecule) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">
          No molecule loaded. Please generate or load a molecule first.
        </p>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
        <p className="text-lg font-medium text-slate-900 dark:text-white mb-2">
          Saved Successfully!
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {name} has been added to your library
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Save Molecule
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Molecule Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter molecule name..."
          className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes about this molecule..."
          rows={3}
          className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>

      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Molecule Details
        </p>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-slate-500 dark:text-slate-400">Formula:</span>
            <span className="ml-2 font-mono text-slate-900 dark:text-white">
              {currentMolecule.formula}
            </span>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400">Atoms:</span>
            <span className="ml-2 text-slate-900 dark:text-white">
              {currentMolecule.atoms.length}
            </span>
          </div>
          <div>
            <span className="text-slate-500 dark:text-slate-400">Bonds:</span>
            <span className="ml-2 text-slate-900 dark:text-white">
              {currentMolecule.bonds.length}
            </span>
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={!name.trim() || isSaving}
        className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
      >
        {isSaving ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-5 h-5" />
            Save to Library
          </>
        )}
      </button>
    </div>
  );
}

interface DownloadPanelProps {
  onClose?: () => void;
}

export function DownloadPanel({ onClose }: DownloadPanelProps) {
  const [format, setFormat] = useState<'png' | 'svg' | 'pdb' | 'mol' | 'sdf' | 'json'>('png');
  const [isDownloading, setIsDownloading] = useState(false);
  
  const { currentMolecule } = useMoleculeStore();
  const { showToast } = useUIStore();

  const handleDownload = useCallback(async () => {
    if (!currentMolecule) {
      showToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Error',
        message: 'No molecule to download',
      });
      return;
    }

    setIsDownloading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      let content: string;
      let mimeType: string;
      let extension: string;

      switch (format) {
        case 'png':
          content = JSON.stringify({ 
            type: 'png', 
            molecule: currentMolecule.name,
            note: 'Canvas screenshot would be generated here'
          }, null, 2);
          mimeType = 'image/png';
          extension = 'png';
          break;
        case 'svg':
          content = generateSVG(currentMolecule);
          mimeType = 'image/svg+xml';
          extension = 'svg';
          break;
        case 'pdb':
          content = generatePDB(currentMolecule);
          mimeType = 'text/plain';
          extension = 'pdb';
          break;
        case 'mol':
          content = generateMOL(currentMolecule);
          mimeType = 'text/plain';
          extension = 'mol';
          break;
        case 'sdf':
          content = generateSDF(currentMolecule);
          mimeType = 'text/plain';
          extension = 'sdf';
          break;
        case 'json':
          content = JSON.stringify(currentMolecule, null, 2);
          mimeType = 'application/json';
          extension = 'json';
          break;
        default:
          content = '';
          mimeType = 'text/plain';
          extension = 'txt';
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${currentMolecule.name.replace(/\s+/g, '_')}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast({
        id: Date.now().toString(),
        type: 'success',
        title: 'Download Started',
        message: `${currentMolecule.name}.${extension} is being downloaded`,
      });

      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (error) {
      showToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Error',
        message: 'Failed to download molecule',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [currentMolecule, format, showToast, onClose]);

  const formats = [
    { id: 'png', name: 'PNG', desc: 'High-res Image', icon: '🖼️' },
    { id: 'svg', name: 'SVG', desc: 'Vector Graphics', icon: '📐' },
    { id: 'pdb', name: 'PDB', desc: 'Protein Data Bank', icon: '🧬' },
    { id: 'mol', name: 'MOL', desc: 'MDL Molfile', icon: '⚛️' },
    { id: 'sdf', name: 'SDF', desc: 'Structure-Data', icon: '📊' },
    { id: 'json', name: 'JSON', desc: 'Structured Data', icon: '📋' },
  ];

  if (!currentMolecule) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">
          No molecule loaded. Please generate or load a molecule first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Export Molecule
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {formats.map((f) => (
          <button
            key={f.id}
            onClick={() => setFormat(f.id as typeof format)}
            className={`p-4 rounded-lg border-2 transition-all ${
              format === f.id
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
            }`}
          >
            <div className="text-2xl mb-2">{f.icon}</div>
            <div className="font-medium text-sm text-slate-900 dark:text-white">
              {f.name}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {f.desc}
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={handleDownload}
        disabled={isDownloading}
        className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
      >
        {isDownloading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Preparing Download...
          </>
        ) : (
          <>
            <Download className="w-5 h-5" />
            Download {format.toUpperCase()}
          </>
        )}
      </button>
    </div>
  );
}

interface SharePanelProps {
  onClose?: () => void;
}

export function SharePanel({ onClose }: SharePanelProps) {
  const [copied, setCopied] = useState(false);
  const [showLink, setShowLink] = useState(false);
  
  const { currentMolecule } = useMoleculeStore();
  const { showToast } = useUIStore();

  const shareUrl = currentMolecule
    ? `${window.location.origin}?molecule=${encodeURIComponent(currentMolecule.name)}`
    : '';

  const handleCopyLink = useCallback(async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showToast({
        id: Date.now().toString(),
        type: 'success',
        title: 'Copied!',
        message: 'Link copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast({
        id: Date.now().toString(),
        type: 'error',
        title: 'Error',
        message: 'Failed to copy link',
      });
    }
  }, [shareUrl, showToast]);

  const handleNativeShare = useCallback(async () => {
    if (!currentMolecule) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: currentMolecule.name,
          text: `Check out this molecule: ${currentMolecule.name}`,
          url: shareUrl,
        });
      } catch (error) {
        // User cancelled or share failed
      }
    } else {
      handleCopyLink();
    }
  }, [currentMolecule, shareUrl, handleCopyLink]);

  if (!currentMolecule) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 dark:text-slate-400">
          No molecule loaded. Please generate or load a molecule first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Share Molecule
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        )}
      </div>

      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {currentMolecule.name}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Formula: {currentMolecule.formula}
        </p>
      </div>

      {'share' in navigator && (
        <button
          onClick={handleNativeShare}
          className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
        >
          <Share2 className="w-5 h-5" />
          Share
        </button>
      )}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          Share Link
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={showLink ? shareUrl : 'Click to show link'}
            readOnly
            className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none"
            onClick={() => setShowLink(true)}
          />
          <button
            onClick={handleCopyLink}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-all"
          >
            {copied ? (
              <Check className="w-5 h-5 text-emerald-500" />
            ) : (
              <Copy className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            )}
          </button>
        </div>
      </div>

      <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
        <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
          Share this link with others to let them view this molecule
        </p>
      </div>
    </div>
  );
}

function generateSVG(molecule: Molecule): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
  <rect width="400" height="400" fill="white"/>
  <text x="200" y="200" text-anchor="middle" font-family="Arial" font-size="24">
    ${molecule.name}
  </text>
  <text x="200" y="240" text-anchor="middle" font-family="Arial" font-size="16">
    ${molecule.formula}
  </text>
</svg>`;
}

function generatePDB(molecule: Molecule): string {
  let pdb = `HEADER    MOLECULE STUDIO AI
TITLE     ${molecule.name}
COMPND    MOLNAME: ${molecule.name}
SOURCE    MOLECULE STUDIO AI
REMARK    GENERATED BY MOLECULE STUDIO
`;
  
  molecule.atoms.forEach((atom, i) => {
    pdb += `ATOM  ${String(i + 1).padStart(5)}  ${atom.element.padEnd(3)} MOL A${String(i + 1).padStart(4)}   ${String(atom.x * 10).padStart(7, ' ')}  ${String(atom.y * 10).padStart(7, ' ')}  ${String(atom.z * 10).padStart(7, ' ')}  1.00  0.00          ${atom.element}
`;
  });

  pdb += 'END\n';
  return pdb;
}

function generateMOL(molecule: Molecule): string {
  const counts = `${String(molecule.atoms.length).padStart(3)} ${String(molecule.bonds.length).padStart(3)}  0  0  0  0    0  00  0999 V2000
`;
  
  let mol = `${molecule.name}
  MOLECULE STUDIO AI
${counts}`;
  
  molecule.atoms.forEach((atom) => {
    mol += `${String(atom.x * 10).padStart(10, ' ')}${String(atom.y * 10).padStart(10, ' ')}${String(atom.z * 10).padStart(10, ' ')} ${atom.element}  0  0  0  0  0  0  0  0  0  0  0  0
`;
  });

  molecule.bonds.forEach((bond) => {
    mol += `${String(bond.atomIndex1 + 1).padStart(3)}${String(bond.atomIndex2 + 1).padStart(3)}  1  0  0  0  0
`;
  });

  mol += 'M  END\n';
  return mol;
}

function generateSDF(molecule: Molecule): string {
  return generateMOL(molecule) + '$$$$\n';
}

